import React from 'react';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { HideIcon, ShowIcon } from '../Icons/CommonIcons';
import ChevronDownSmall from '@/components/Icons/ChevronDownSmall';
import clsx from 'clsx';
import { SignupBanner } from '../Banner';

/**
 * Renders an input field with consistent styling.
 */
interface ExtendedInputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  description?: string;
  error?: boolean;
  disabled?: boolean;
}

export function InputField({
  label,
  className,
  description,
  error,
  disabled,
  ...props
}: ExtendedInputFieldProps) {
  return (
    <div>
      <div
        className={`group relative py-12 px-16 w-full rounded-2xs border ${error ? 'border-secondary-red-500' : 'border-neutral-200 focus-within:border-neutral-500'}`}
      >
        {label && (
          <label
            className={`absolute -translate-y-1/2 bg-white text-neutral-400 pointer-events-none duration-200 group-focus-within:top-0 group-focus-within:text-xs group-focus-within:px-2 group-focus-within:left-14 ${props.value ? 'top-0 text-xs px-2 left-14' : 'left-16 top-1/2 z-0'}`}
          >
            {label}
          </label>
        )}
        <input
          className={clsx(
            'z-10 text-body w-full outline-none',
            className ?? '',
            disabled ? 'text-neutral-400' : 'text-neutral-500'
          )}
          autoComplete="none"
          {...props}
          disabled={disabled}
        />
      </div>
      {description && <p className="mt-4 text-xs text-neutral-400">{description}</p>}
    </div>
  );
}

/**
 * Renders a select dropdown for organizations.
 */
export function OrganizationSelect({
  value,
  onChange,
  organizations
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  organizations: { id: string; name: string; tenant: string }[];
}) {
  return (
    <div className="relative">
      <select
        className={`
        w-full rounded-2xs border border-neutral-200 py-12 pl-16 pr-40 text-neutral-400 font-light appearance-none
        focus:outline-none focus:ring-0 focus:shadow-none focus:border-black
      `}
        name="organization"
        value={value}
        onChange={onChange}
      >
        <option value="" disabled>
          Your Organization
        </option>
        {organizations.map(({ id, tenant, name }) => (
          <option key={id} value={tenant}>
            {name}
          </option>
        ))}
      </select>
      <ChevronDownSmall className="text-neutral-500 pointer-events-none absolute right-14 top-14 w-24 h-24" />
    </div>
  );
}

/**
 * Renders a password input field with toggle visibility button.
 */
export function PasswordField({
  value,
  onChange,
  showPassword,
  togglePasswordVisibility,
  label = 'Password',
  error
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showPassword: boolean;
  togglePasswordVisibility: () => void;
  label?: string;
  error?: boolean;
}) {
  return (
    <div>
      <div className="relative">
        <InputField
          aria-label={label}
          type={showPassword ? 'text' : 'password'}
          name="password"
          label={label}
          value={value}
          onChange={onChange}
          error={error}
        />
        <button
          type="button"
          onClick={togglePasswordVisibility}
          className={`
            absolute right-16 top-1/2 transform -translate-y-1/2
            text-slate-400 hover:text-pink-500
            transition-colors duration-200 focus:outline-none
          `}
        >
          {showPassword ? <HideIcon /> : <ShowIcon />}
        </button>
      </div>
    </div>
  );
}

/**
 * Renders a submit button with loading state.
 */
export function SubmitButton({
  title,
  loading,
  onSubmit
}: {
  title: string;
  loading: boolean;
  onSubmit?: (event: React.FormEvent) => Promise<void>;
}) {
  return (
    <button
      aria-label={title}
      className={`
        btn btn-primary
        mt-4 mb-4
      `}
      disabled={loading}
      onClick={onSubmit}
    >
      {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : title}
    </button>
  );
}

/**
 * Renders a link component with consistent styling.
 */
function StyledLink({ label, linkText, to }: { to: string; label?: string; linkText: string }) {
  return (
    <div className="flex flex-row justify-center">
      <span className="text-sm text-neutral-400 font-light">{label}</span>
      &nbsp;
      <Link className="link link-sm font-semibold" to={to}>
        {linkText}
      </Link>
    </div>
  );
}

export const SignInLink = () => (
  <StyledLink to="/auth/sign-in" label="Already have an account?" linkText="Sign In" />
);

export const SignUpLink = () => (
  <StyledLink to="/auth/sign-up" label="Don't have an account?" linkText="Sign Up" />
);

export const ResetPasswordLink = () => (
  <StyledLink to="/auth/request-reset" label="Forgot the password?" linkText="Reset Password" />
);

/**
 * Renders a read-only username field.
 */
export function ReadOnlyUsernameField({ username }: { username: string }) {
  return (
    <InputField
      id="userName"
      name="userName"
      value={username}
      readOnly
      className={`
        w-full rounded-full p-2 my-2
        border border-slate-300 bg-slate-100
        text-slate-600 cursor-not-allowed
      `}
    />
  );
}

/**
 * Container component for form layout.
 */
export function Container({
  children,
  banner,
  bannerVariant = 'error',
  title,
  subtitle,
  className = 'w-full max-w-xl'
}: {
  children: React.ReactNode;
  banner?: string;
  bannerVariant?: 'error' | 'success';
  title: string;
  subtitle?: string | React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`p-6 mb-6 space-y-2 ${className}`}>
      <h2 className="text-h2 text-center">{title}</h2>
      {subtitle && (
        <p className="text-neutral-400 text-center mt-[4px] mb-[24px] font-light">{subtitle}</p>
      )}
      <SignupBanner text={banner ?? ''} variant={bannerVariant} visible={!!banner} />
      {children}
    </div>
  );
}
